// src/context/AuthProvider.jsx
import React, { createContext, useState, useEffect, useContext } from "react";
import api from "../services/api";
import { getMe } from "../features/account/data/user.service";
import i18n from "../i18n";

export const AuthContext = createContext({
    isAuthenticated: false,
    user: null,
    login: async () => false,
    logout: () => { },
    isLoading: true,
    confirmRegistration: async () => ({ success: false }),
    resendCode: async () => ({ success: false }),
    vkLogin: async () => ({ success: false }),
    socialLoginLoading: false,
});

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [socialLoginLoading, setSocialLoginLoading] = useState(false);

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                setIsLoading(false);
                return;
            }

            try {
                setIsAuthenticated(true);
                // 👇 aquí cargamos el perfil actual
                const res = await getMe();
                setUser(res || null);
            } catch (error) {
                console.error("Error cargando current user:", error);
                // Token inválido → limpiamos
                localStorage.removeItem("token");
                setIsAuthenticated(false);
                setUser(null);
            } finally {
                setIsLoading(false);
            }
        };

        initAuth();
    }, []);

    // Sync i18n with user preferred language
    useEffect(() => {
        if (user?.language) {
            i18n.changeLanguage(user.language);
        }
    }, [user?.language]);

    const login = async (credentials) => {
        try {
            const response = await api.post("/api/v1/login", credentials);
            const { token, user: userData } = response.data;

            localStorage.setItem("token", token);

            // Fetch full profile immediately
            try {
                const fullUser = await getMe();
                const u = fullUser || userData || null;
                setUser(u);
            } catch (e) {
                const u = userData || null;
                setUser(u);
            }

            setIsAuthenticated(true);
            return { success: true };
        } catch (error) {
            console.error("Error en login:", error);
            return { success: false, error: error.response?.data?.error };
        }
    };

    const signup = async (userData) => {
        try {
            let headers = {};
            try {
                // Fetch IP with 2s timeout
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 2000);

                const response = await fetch('https://api.ipify.org?format=json', {
                    signal: controller.signal
                });
                clearTimeout(timeoutId);

                if (response.ok) {
                    const data = await response.json();
                    if (data.ip) {
                        headers['X-Client-IP'] = data.ip;
                    }
                }
            } catch (e) {
                // Fail silently if IP fetch fails (timeout or blocking)
                console.warn("Could not fetch client IP for signup headers:", e);
            }

            await api.post("/api/v1/signup", { user: userData }, { headers });
            return { success: true };
        } catch (error) {
            console.error("Error en signup:", error);
            let msg = null;
            const data = error.response?.data;
            if (data?.errors) {
                msg = Array.isArray(data.errors) ? data.errors.join(", ") : JSON.stringify(data.errors);
            } else if (data?.error) {
                msg = data.error;
            } else {
                msg = error.message;
            }
            return { success: false, error: msg };
        }
    };

    const confirmRegistration = async (email, otp_code) => {
        try {
            const response = await api.post("/api/v1/confirm", { email, otp_code });
            const { token, user: userData } = response.data;

            if (token) {
                localStorage.setItem("token", token);

                // Fetch full profile immediately
                try {
                    const fullUser = await getMe();
                    const u = fullUser || userData || null;
                    setUser(u);
                } catch (e) {
                    const u = userData || null;
                    setUser(u);
                }

                setIsAuthenticated(true);
            }
            return { success: true, message: response.data.message };
        } catch (error) {
            console.error("Error en confirmación:", error);
            return { success: false, error: error.response?.data?.error || error.response?.data?.message || "Error confirming account" };
        }
    };

    const resendCode = async (email) => {
        try {
            const response = await api.post("/api/v1/resend_code", { email });
            return { success: true, message: response.data.message };
        } catch (error) {
            console.error("Error reenviando código:", error);
            return { success: false, error: error.response?.data?.error || error.response?.data?.message || "Error resending code" };
        }
    };

    const logout = () => {
        localStorage.removeItem("token");
        setIsAuthenticated(false);
        setUser(null);
        window.location.href = "/";
    };

    const telegramLogin = async (user) => {
        setSocialLoginLoading(true);
        try {
            const response = await api.post("/api/v1/telegram_sessions", user);
            const { token, user: userData } = response.data;

            localStorage.setItem("token", token);
            setIsAuthenticated(true);

            // Try to get full profile, fall back to basic data
            try {
                const fullUser = await getMe();
                const u = fullUser || userData || null;
                setUser(u);
            } catch (e) {
                const u = userData || null;
                setUser(u);
            }

            return { success: true };
        } catch (error) {
            console.error("Error en telegram login:", error);
            return { success: false, error: error.response?.data?.error || "Telegram login failed" };
        } finally {
            setSocialLoginLoading(false);
        }
    };

    const vkLogin = async (vkData) => {
        setSocialLoginLoading(true);
        try {
            const response = await api.post("api/v1/vk_login", vkData);
            const { token, user: userData } = response.data;

            localStorage.setItem("token", token);
            setIsAuthenticated(true);

            try {
                const fullUser = await getMe();
                setUser(fullUser || userData || null);
            } catch (e) {
                setUser(userData || null);
            }

            return { success: true };
        } catch (error) {
            console.error("Error en VK login:", error);
            return { success: false, error: error.response?.data?.error || "VK login failed" };
        } finally {
            setSocialLoginLoading(false);
        }
    };

    const forgotPassword = async (email) => {
        try {
            const response = await api.post("/api/v1/password/forgot", { email });
            return { success: true, message: response.data.message };
        } catch (error) {
            console.error("Error en forgot password:", error);
            // Default error message
            let msg = "Error requesting password reset";
            if (error.response?.data?.error) msg = error.response.data.error;
            else if (error.response?.data?.message) msg = error.response.data.message;
            return { success: false, error: msg };
        }
    };

    const resetPassword = async (data) => {
        try {
            const response = await api.put("/api/v1/password/reset", data);
            const { token, user: userData } = response.data;

            if (token) {
                localStorage.setItem("token", token);

                // Try to get full profile
                try {
                    const fullUser = await getMe();
                    const u = fullUser || userData || null;
                    setUser(u);
                } catch (e) {
                    const u = userData || null;
                    setUser(u);
                }

                setIsAuthenticated(true);
            }

            return { success: true, message: response.data.message };
        } catch (error) {
            console.error("Error en reset password:", error);
            // Default error message
            let msg = "Error resetting password";
            const errData = error.response?.data;

            if (errData?.errors) {
                msg = Array.isArray(errData.errors) ? errData.errors.join(", ") : JSON.stringify(errData.errors);
            } else if (errData?.error) {
                msg = errData.error;
            } else if (errData?.message) {
                msg = errData.message;
            }

            return { success: false, error: msg };
        }
    };

    return (
        <AuthContext.Provider
            value={{ isAuthenticated, user, login, logout, signup, confirmRegistration, resendCode, telegramLogin, vkLogin, forgotPassword, resetPassword, isLoading, socialLoginLoading }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// 👇 hook práctico para usar el contexto
export const useAuth = () => useContext(AuthContext);